import { type PropsWithChildren, createContext, useContext } from 'react'

import { Stack, type SxProps, type Theme } from '@mui/material'

import Footer from '~/Footer'
import Header from '~/Header'
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
} from '~/features/navigation/components/SidebarMenu'
import { useSidebarCollapsed } from '~/features/navigation/hooks/useSidebarCollapsed'

interface MainLayoutProps extends PropsWithChildren {
  hasGrayBackground?: boolean
  contentMarginTop?: string
  sx?: SxProps<Theme>
}

const MainLayoutContext = createContext(false)

export function useIsInsideMainLayout() {
  return useContext(MainLayoutContext)
}

export default function MainLayout({
  sx,
  children,
  hasGrayBackground,
  contentMarginTop = '72px',
}: MainLayoutProps) {
  const isInsideMainLayout = useIsInsideMainLayout()
  const { sidebarCollapsed, toggleSidebar } = useSidebarCollapsed()

  if (isInsideMainLayout) return <>{children}</>

  return (
    <MainLayoutContext.Provider value>
      <Stack
        sx={{
          minHeight: '100vh',
          marginLeft: {
            xs: 0,
            md: `${sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}px`,
          },
          backgroundColor: hasGrayBackground
            ? theme => theme.palette.background.paper
            : undefined,
          ...sx,
        }}
      >
        <Header
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
        <Stack sx={{ flex: 1, marginTop: contentMarginTop, pt: 2 }}>
          {children}
        </Stack>
        <Footer />
      </Stack>
    </MainLayoutContext.Provider>
  )
}
